import React from 'react'
import styled from '@emotion/styled'
import { radius } from '@mobi/component-library/Foundations'
import { ChannelType, SkyTvSvg } from '@mobi/component-library/Assets/Svg/SkyTv/SkyTvSvg'
import { FeatureFlags, hexColors } from '@mobi/settings'
import { useFeature } from '@core/Utils/hooks'
import { useAppDispatch } from '@core/Store/hooks'
import { setSelectedChannel, type SkyVideoPlayerState } from '@core/Areas/SkyVideoPlayer/Store'
import { PlayerControlButtonStyled } from '../Buttons'
import { trackSelectedChannel } from '@core/Areas/SkyVideoPlayer/analytics'

const enum LocalConstants {
  Sky2ActiveClass = 'js-sky-channels--sky2-active',
  RWAActiveClass = 'js-sky-channels--rwa-active',
}

export const SkyChannelSelector: React.FC<{
  selectedChannel: SkyVideoPlayerState['selectedChannel']
}> = ({ selectedChannel }) => {
  const dispatch = useAppDispatch()
  const isRacingWAFeatureActive = useFeature(FeatureFlags.RACING_WA_VISION_CHANNEL.key)

  const activeChannelClass =
    selectedChannel === 'Sky2'
      ? LocalConstants.Sky2ActiveClass
      : selectedChannel === 'RacingWA'
        ? LocalConstants.RWAActiveClass
        : ''

  const createChannelClickHandler =
    (channel: ChannelType) => (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation()
      dispatch(setSelectedChannel(channel))
      trackSelectedChannel(channel)
    }

  return (
    <WrapperStyled>
      <PlayerSkyChannelButtonStyled
        onClick={createChannelClickHandler('Sky1')}
        data-testid='SkyChannelSelector.Sky1'
      >
        <SkyTvSvg channel='Sky1' width='3.6rem' colorOverride='white' />
      </PlayerSkyChannelButtonStyled>

      <PlayerSkyChannelButtonStyled
        onClick={createChannelClickHandler('Sky2')}
        data-testid='SkyChannelSelector.Sky2'
      >
        <SkyTvSvg channel='Sky2' width='3.6rem' colorOverride='white' />
      </PlayerSkyChannelButtonStyled>

      {isRacingWAFeatureActive && (
        <PlayerSkyChannelButtonStyled
          onClick={createChannelClickHandler('RacingWA')}
          data-testid='SkyChannelSelector.RacingWA'
        >
          <SkyTvSvg channel='RacingWA' width='3.6rem' colorOverride='white' />
        </PlayerSkyChannelButtonStyled>
      )}

      <span aria-hidden className={activeChannelClass} />
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  boxSizing: 'border-box',
  flexShrink: 0,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  height: '3.6rem',
  padding: '0.5rem',
  background: 'rgba(0,0,0,0.6)',
  borderRadius: radius.md,

  '> button': {
    flexShrink: 0,
  },

  // Active indicator
  '> span': {
    position: 'absolute',
    left: '0.5rem',
    minWidth: '5rem',
    minHeight: '2.6rem',
    transition: '0.2s',
    background: hexColors.skyTvRed,
    borderRadius: radius.sm,

    [`&.${LocalConstants.Sky2ActiveClass}`]: {
      transform: 'translateX(5rem)',
      background: hexColors.skyTvBlue,
    },

    [`&.${LocalConstants.RWAActiveClass}`]: {
      transform: 'translateX(10rem)',
      background: hexColors.racingWa,
    },
  },
})

const PlayerSkyChannelButtonStyled = styled(PlayerControlButtonStyled)({
  position: 'relative',
  height: '2.6rem',
  width: '5rem',
  padding: 0,
  background: 0,
})
